import type { SharedModels } from '@boostbrothers/web-api';
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { type FC, type PropsWithChildren, useMemo } from 'react';

import useToast from '~hooks/useToast';
import useViewTransition from '~hooks/useViewTransition';

const QueryProvider: FC<PropsWithChildren> = ({ children }) => {
  const { addToast } = useToast();
  const { navigateWithTransition } = useViewTransition();

  const queryClient = useMemo(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: error => {
            const {
              error: { status },
            } = error as unknown as SharedModels.ErrorRes;

            if (status === 401) {
              addToast({
                title: '토큰이 만료됐어요. 다시 로그인해주세요.',
                description: '잠시 후 로그인 화면으로 이동합니다.',
                state: 'error',
                position: 'bottom-center',
              });
              localStorage.removeItem('userToken');
              sessionStorage.removeItem('userToken');

              setTimeout(() => {
                navigateWithTransition('/login');
              }, 1000);
              return;
            }

            if (status === 400 || status === 404) {
              addToast({
                title: '존재하지 않는 페이지 또는 잘못된 접근입니다.',
                state: 'error',
                position: 'bottom-center',
              });

              // PA에서 진입했을 경우, 아예 로그아웃 처리. 아닐 경우, 홈으로 리다이렉트.
              if (sessionStorage.getItem('userToken')) {
                sessionStorage.removeItem('userToken');
                navigateWithTransition('/login');
              } else {
                navigateWithTransition('/');
              }
              return;
            }

            if (status >= 500) {
              addToast({
                title: '일시적인 문제가 발생했어요. 잠시 후 다시 시도해주세요.',
                state: 'error',
                position: 'bottom-center',
              });
            }
          },
        }),
        defaultOptions: {
          queries: {
            retry: 0,
            refetchOnWindowFocus: false,
          },
        },
      }),
    []
  );

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};

export default QueryProvider;
